import Cookies from "js-cookie";
import {
    AlertCircleIcon,
    AlertTriangleIcon,
    ArrowRightIcon,
    CheckCircleIcon,
    CircleDotIcon,
    ClockIcon,
    DatabaseIcon,
    Loader2Icon,
    PlayIcon,
    RefreshCwIcon,
    SquareIcon,
    UploadIcon,
    XCircleIcon,
} from "lucide-react";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";

import {Badge} from "@components/ui/badge";
import {Button} from "@components/ui/button";
import {Card, CardContent} from "@components/ui/card";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@components/ui/select";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@components/ui/tooltip";
import {
    type EntitySyncStatus,
    clearEntitySync,
    stopEntitySync,
    triggerEntitySync,
    triggerSyncAll,
    useVNextSyncStatusAll,
} from "@hooks/use-vnext";

interface DataSyncTabProps {
    workspaceId: string;
    scenarioId: string;
    autoTriggerSyncAll?: boolean;
}

// ── Entity metadata ──────────────────────────────────────────

interface EntityMeta {
    label: string;
    description: string;
    feeds: string;
    recordLabel: string;
    hasLookback?: boolean; // show lookback dropdown
}

const ENTITY_META: Record<string, EntityMeta> = {
    accounts: {
        label: "Chart of Accounts",
        description: "Account codes, types, and reporting codes from Xero",
        feeds: "Account Mapping, P&L categories",
        recordLabel: "accounts",
    },
    contacts: {
        label: "Contacts",
        description: "Customers, suppliers, and other contacts",
        feeds: "Contact Mapping, invoice breakdowns",
        recordLabel: "contacts",
    },
    bank_transactions: {
        label: "Bank Transactions",
        description: "Payments received and spent across all bank accounts",
        feeds: "Cash Flow Explorer",
        recordLabel: "transactions",
        hasLookback: true,
    },
    invoices: {
        label: "Invoices",
        description: "Accounts receivable and accounts payable invoices",
        feeds: "AR/AP analysis, aged receivables",
        recordLabel: "invoices",
        hasLookback: true,
    },
    journals: {
        label: "Journal Entries",
        description: "General ledger journal lines — the core accounting data",
        feeds: "P&L Explorer, Cash Flow Summary, KPI snapshots",
        recordLabel: "journal lines",
        hasLookback: true,
    },
    report_profit_and_loss: {
        label: "P&L Report",
        description: "Monthly Profit & Loss from Xero — used as check figures",
        feeds: "P&L reconciliation, check figures",
        recordLabel: "months",
        hasLookback: true,
    },
    report_balance_sheet: {
        label: "Balance Sheet",
        description: "Monthly Balance Sheet snapshots from Xero",
        feeds: "BS reconciliation, check figures",
        recordLabel: "months",
        hasLookback: true,
    },
    report_bank_summary: {
        label: "Bank Summary",
        description: "Monthly bank receipts, payments, and balances",
        feeds: "Cash flow reconciliation, check figures",
        recordLabel: "months",
        hasLookback: true,
    },
};

const ENTITY_ORDER = [
    "accounts", "contacts", "bank_transactions", "invoices", "journals",
    "report_profit_and_loss", "report_balance_sheet", "report_bank_summary",
];

const REPORT_SECTION_START = 5; // index in ENTITY_ORDER where report entities begin

const EOH_ENTITY_ORDER = ["offices", "clients", "bookings", "shifts", "members"];

const EOH_LOOKBACK_OPTIONS = [1, 3, 7, 14, 30, 60, 90];

const EOH_ENTITY_LABELS: Record<string, {label: string; description: string; recordLabel: string; hasLookback?: boolean}> = {
    offices: {label: "Offices", description: "Branch/office lookup data", recordLabel: "offices"},
    clients: {label: "Clients", description: "Service locations and client master data", recordLabel: "clients"},
    bookings: {label: "Booking Trends", description: "Daily ordered/booked/cancelled aggregates", recordLabel: "booking rows", hasLookback: true},
    shifts: {label: "Shifts", description: "Shift-level activity for reporting", recordLabel: "shifts", hasLookback: true},
    members: {label: "Members", description: "Worker profile and qualification data", recordLabel: "members"},
};

interface EohStatusData {
    configured: boolean;
    enabled: boolean;
    tenantId: string;
}

interface EohSyncLogStatus {
    syncType: string;
    status: "running" | "completed" | "failed" | "skipped" | null;
    recordsSynced: number;
    recordCount: number;
    startedAt: string | null;
    completedAt: string | null;
    errorMessage: string;
    cooldownRemainingSeconds?: number;
}

interface EohSyncStatusData {
    enabled: boolean;
    configured: boolean;
    tenantId: string;
    isRunning: boolean;
    logs: Record<string, EohSyncLogStatus>;
}

// ── Helpers ──────────────────────────────────────────────────

function formatRelativeTime(iso: string | null): string {
    if (!iso) return "Never";
    const diffMs = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diffMs / 60_000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
}

function formatDateTime(iso: string | null): string {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString("en-AU", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function formatDuration(startIso: string | null, endIso: string | null): string | null {
    if (!startIso || !endIso) return null;
    const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
    if (ms < 1000) return "<1s";
    const secs = Math.floor(ms / 1000);
    if (secs < 60) return `${secs}s`;
    const mins = Math.floor(secs / 60);
    const remainSecs = secs % 60;
    if (mins < 60) return `${mins}m ${remainSecs}s`;
    const hrs = Math.floor(mins / 60);
    return `${hrs}h ${mins % 60}m`;
}

function formatNumber(n: number): string {
    return n.toLocaleString();
}

type StatusColor = "green" | "amber" | "blue" | "red" | "gray";

// A sync is only "stale" once a new month has closed since it last completed —
// i.e. it ran before the 1st of the current month. Day-old data is still current
// for monthly reporting, so flagging "Stale" after 24h just causes false alarms.
function isStaleSince(completedIso: string): boolean {
    const completed = new Date(completedIso);
    const now = new Date();
    const startOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    return completed < startOfCurrentMonth;
}

function getStatusInfo(entity: EntitySyncStatus): {color: StatusColor; label: string} {
    if (entity.status === "running") return {color: "blue", label: "Syncing"};
    if (entity.status === "error") return {color: "red", label: "Error"};
    if (entity.status === "cancelled") return {color: "amber", label: "Cancelled"};
    if (entity.needs_continuation) return {color: "amber", label: "Incomplete"};
    if (entity.status === "partial") return {color: "amber", label: "Partial"};
    if (entity.status === "success") {
        if (entity.completed_at && isStaleSince(entity.completed_at)) {
            return {color: "amber", label: "Stale"};
        }
        return {color: "green", label: "Synced"};
    }
    return {color: "gray", label: "Not synced"};
}

function getEohStatusInfo(entity: EohSyncLogStatus): {color: StatusColor; label: string} {
    if (entity.status === "running") return {color: "blue", label: "Syncing"};
    if (entity.status === "failed") return {color: "red", label: "Error"};
    if (entity.status === "completed") {
        if (entity.completedAt && isStaleSince(entity.completedAt)) {
            return {color: "amber", label: "Stale"};
        }
        return {color: "green", label: "Synced"};
    }
    if (entity.status === "skipped") return {color: "amber", label: "Skipped"};
    return {color: "gray", label: "Not synced"};
}

const STATUS_BADGE_CLASSES: Record<StatusColor, string> = {
    green: "bg-green-100 text-green-700 border-green-200",
    amber: "bg-amber-100 text-amber-700 border-amber-200",
    blue: "bg-blue-100 text-blue-700 border-blue-200",
    red: "bg-red-100 text-red-700 border-red-200",
    gray: "bg-gray-100 text-gray-500 border-gray-200",
};

const STATUS_ICONS: Record<StatusColor, React.ReactNode> = {
    green: <CheckCircleIcon className="size-3.5" />,
    amber: <AlertTriangleIcon className="size-3.5" />,
    blue: <Loader2Icon className="size-3.5 animate-spin" />,
    red: <XCircleIcon className="size-3.5" />,
    gray: <CircleDotIcon className="size-3.5" />,
};

// ── Step Row ─────────────────────────────────────────────────

const LOOKBACK_OPTIONS = [
    {value: "1", label: "Last 1 month"},
    {value: "3", label: "Last 3 months"},
    {value: "6", label: "Last 6 months"},
    {value: "12", label: "Last 12 months"},
    {value: "24", label: "Last 24 months"},
    {value: "36", label: "Last 3 years"},
    {value: "0", label: "All history"},
];

function SyncStepRow({
    stepNumber,
    entity,
    meta,
    lookbackMonths,
    onLookbackChange,
    onSync,
    onStop,
    onClear,
}: {
    stepNumber: number;
    entity: EntitySyncStatus;
    meta: EntityMeta;
    lookbackMonths: number;
    onLookbackChange: (months: number) => void;
    onSync: () => void;
    onStop: () => void;
    onClear: () => void;
}) {
    const isRunning = entity.status === "running";
    const startedMinutesAgo = entity.started_at
        ? (Date.now() - new Date(entity.started_at).getTime()) / 60_000
        : 0;
    const showClear = isRunning && startedMinutesAgo > 5;
    const {color, label} = getStatusInfo(entity);
    const duration = formatDuration(entity.started_at, entity.completed_at);
    const hasSynced = entity.status != null && entity.status !== "running";

    return (
        <Card>
            <CardContent className="p-4">
                <div className="flex items-start gap-4">
                    {/* Step number */}
                    <div className="flex items-center justify-center size-7 rounded-full bg-muted text-xs font-semibold text-muted-foreground shrink-0 mt-0.5">
                        {stepNumber}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0 space-y-2">
                        {/* Title row */}
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <h3 className="text-sm font-semibold leading-tight">{meta.label}</h3>
                                <p className="text-xs text-muted-foreground mt-0.5">{meta.description}</p>
                                <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                                    <ArrowRightIcon className="size-3 shrink-0" />
                                    Feeds: {meta.feeds}
                                </p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                <Badge variant="outline" className={`text-xs gap-1 ${STATUS_BADGE_CLASSES[color]}`}>
                                    {STATUS_ICONS[color]}
                                    {label}
                                </Badge>
                            </div>
                        </div>

                        {/* Live progress */}
                        {isRunning && (
                            <div className="text-xs text-blue-600 space-y-0.5 pl-0.5">
                                {entity.pages_fetched != null && (
                                    <p>Page {entity.pages_fetched} fetched</p>
                                )}
                                {entity.records_synced > 0 && (
                                    <p>{formatNumber(entity.records_synced)} {meta.recordLabel} so far</p>
                                )}
                                {entity.started_at && (
                                    <p className="text-muted-foreground">Started {formatRelativeTime(entity.started_at)}</p>
                                )}
                            </div>
                        )}

                        {/* Last sync summary */}
                        {!isRunning && hasSynced && (
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground pl-0.5">
                                <span className="flex items-center gap-1">
                                    <DatabaseIcon className="size-3" />
                                    <span className="font-medium">{formatNumber(entity.record_count)}</span> {meta.recordLabel}
                                </span>
                                {entity.records_synced > 0 && (
                                    <span className="flex items-center gap-1">
                                        <RefreshCwIcon className="size-3" />
                                        {formatNumber(entity.records_synced)} last run
                                    </span>
                                )}
                                {entity.completed_at && (
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <span className="flex items-center gap-1 cursor-help">
                                                    <ClockIcon className="size-3" />
                                                    {formatRelativeTime(entity.completed_at)}
                                                </span>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p className="text-xs">{formatDateTime(entity.completed_at)}</p>
                                                {duration && <p className="text-xs">Duration: {duration}</p>}
                                                {entity.pages_fetched != null && entity.pages_fetched > 0 && (
                                                    <p className="text-xs">{entity.pages_fetched} page{entity.pages_fetched !== 1 ? "s" : ""} fetched</p>
                                                )}
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                )}
                                {entity.needs_continuation && (
                                    <span className="text-amber-600 font-medium">Incomplete — needs full sync</span>
                                )}
                            </div>
                        )}

                        {/* Not synced */}
                        {!isRunning && !hasSynced && (
                            <p className="text-xs text-muted-foreground pl-0.5">No data synced yet</p>
                        )}

                        {/* Error detail */}
                        {entity.error_detail && (entity.status === "error" || entity.status === "cancelled") && (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <p className="text-xs text-red-600 truncate cursor-help pl-0.5">
                                            {entity.error_detail.split("\n")[0].slice(0, 100)}
                                        </p>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-sm">
                                        <p className="text-xs whitespace-pre-wrap">{entity.error_detail}</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}

                        {/* Actions */}
                        <div className="flex items-center gap-1.5 pt-1">
                            {!isRunning && meta.hasLookback && (
                                <Select value={String(lookbackMonths)} onValueChange={(v) => onLookbackChange(Number(v))}>
                                    <SelectTrigger className="h-7 w-[140px] text-xs">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {LOOKBACK_OPTIONS.map((opt) => (
                                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                            {!isRunning && (
                                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onSync}>
                                    <RefreshCwIcon className="size-3 mr-1" />
                                    Sync Now
                                </Button>
                            )}
                            {isRunning && (
                                <Button size="sm" variant="outline" className="h-7 text-xs text-amber-600 border-amber-300 hover:bg-amber-50" onClick={onStop}>
                                    <SquareIcon className="size-3 mr-1" />
                                    Stop
                                </Button>
                            )}
                            {showClear && (
                                <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-300 hover:bg-red-50" onClick={onClear}>
                                    <XCircleIcon className="size-3 mr-1" />
                                    Force Clear
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

function EohSyncSection({
    status,
    syncStatus,
    error,
    onSync,
    onForwardSync,
    onCancel,
    isStarting,
    lookbacks,
    onLookbackChange,
}: {
    status: EohStatusData | null;
    syncStatus: EohSyncStatusData | null;
    error: string | null;
    onSync: (syncType: string, lookbackDays?: number) => void;
    onForwardSync: (daysAhead?: number) => void;
    onCancel: (entityType?: string) => void;
    isStarting: string | null;
    lookbacks: Record<string, number>;
    onLookbackChange: (syncType: string, lookbackDays: number) => void;
}) {
    if (!status?.enabled) return null;

    const logs = syncStatus?.logs ?? {};
    const isRunning = Boolean(syncStatus?.isRunning);
    const totalRecords = EOH_ENTITY_ORDER.reduce(
        (sum, key) => sum + (logs[key]?.recordCount ?? 0),
        0,
    );
    const lastSync = EOH_ENTITY_ORDER
        .map((key) => logs[key]?.completedAt)
        .filter(Boolean)
        .sort()
        .pop() ?? null;

    return (
        <div className="space-y-3">
            <div className="pt-3 pb-1">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">EOH Operations</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                    Local cache for Entire OnHire data. The Virtual CFO reads this cache before calling the live API.
                </p>
            </div>

            <Card>
                <CardContent className="p-4 space-y-4">
                    <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                            <div className="flex items-center gap-2">
                                <h3 className="text-sm font-semibold leading-tight">Entire OnHire Sync</h3>
                                <Badge variant="outline" className="text-xs bg-teal-50 text-teal-700 border-teal-200">
                                    Tenant gated
                                </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">
                                Sync one EOH dataset at a time. EOH throttles key APIs, so start small and wait out cooldowns before retrying.
                            </p>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground mt-2">
                                <span className="flex items-center gap-1">
                                    <DatabaseIcon className="size-3" />
                                    {formatNumber(totalRecords)} EOH records
                                </span>
                                <span className="flex items-center gap-1">
                                    <ClockIcon className="size-3" />
                                    Last sync {formatRelativeTime(lastSync)}
                                </span>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <Button
                                onClick={() => onForwardSync(90)}
                                disabled={isRunning || Boolean(isStarting)}
                                variant="outline"
                                size="sm"
                                className="h-8 text-xs"
                            >
                                <PlayIcon className="size-3 mr-1" />
                                Sync Forward 90 Days
                            </Button>
                            {isRunning && (
                                <Button
                                    onClick={() => onCancel()}
                                    variant="outline"
                                    size="sm"
                                    className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50"
                                >
                                    <SquareIcon className="size-3 mr-1" />
                                    Stop All
                                </Button>
                            )}
                        </div>
                    </div>

                    {error && (
                        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">
                            <AlertCircleIcon className="size-4 shrink-0" />
                            {error}
                        </div>
                    )}

                    <div className="grid gap-3 md:grid-cols-5">
                        {EOH_ENTITY_ORDER.map((key) => {
                            const meta = EOH_ENTITY_LABELS[key];
                            const log = logs[key] ?? {
                                syncType: key,
                                status: null,
                                recordsSynced: 0,
                                recordCount: 0,
                                startedAt: null,
                                completedAt: null,
                                errorMessage: "",
                                cooldownRemainingSeconds: 0,
                            };
                            const {color, label} = getEohStatusInfo(log);
                            const cooldownRemaining = log.cooldownRemainingSeconds ?? 0;
                            const isCoolingDown = cooldownRemaining > 0 && log.status !== "running";
                            const isThisStarting = isStarting === key;
                            return (
                                <div key={key} className="rounded-md border bg-card p-3 space-y-2">
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="text-sm font-medium">{meta.label}</div>
                                        <Badge variant="outline" className={`text-xs gap-1 ${STATUS_BADGE_CLASSES[color]}`}>
                                            {STATUS_ICONS[color]}
                                            {label}
                                        </Badge>
                                    </div>
                                    <div className="text-xs text-muted-foreground min-h-8">
                                        {meta.description}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                        <span className="font-medium text-foreground">{formatNumber(log.recordCount)}</span> {meta.recordLabel}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                        Last run: {formatNumber(log.recordsSynced)} synced
                                    </div>
                                    {log.completedAt && (
                                        <div className="text-xs text-muted-foreground">
                                            {formatRelativeTime(log.completedAt)}
                                        </div>
                                    )}
                                    {log.errorMessage && (log.status === "failed" || log.status === "skipped") && (
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <div className="text-xs text-red-600 truncate cursor-help">
                                                        {log.errorMessage.slice(0, 80)}
                                                    </div>
                                                </TooltipTrigger>
                                                <TooltipContent className="max-w-sm">
                                                    <p className="text-xs whitespace-pre-wrap">{log.errorMessage}</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    )}
                                    {isCoolingDown && (
                                        <div className="text-xs text-amber-600">
                                            Cooldown: ~{Math.ceil(cooldownRemaining / 60)}m
                                        </div>
                                    )}
                                    {meta.hasLookback && (
                                        <Select
                                            value={String(lookbacks[key] ?? 7)}
                                            onValueChange={(value) => onLookbackChange(key, Number(value))}
                                            disabled={isRunning || Boolean(isStarting)}
                                        >
                                            <SelectTrigger className="h-8 text-xs">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {EOH_LOOKBACK_OPTIONS.map((days) => (
                                                    <SelectItem key={days} value={String(days)}>
                                                        Last {days} days
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    )}
                                    <Button
                                        onClick={() => onSync(key, meta.hasLookback ? lookbacks[key] ?? 7 : undefined)}
                                        disabled={isRunning || Boolean(isStarting) || isCoolingDown}
                                        variant="outline"
                                        className="h-8 w-full text-xs"
                                        size="sm"
                                    >
                                        {isThisStarting || log.status === "running" ? (
                                            <Loader2Icon className="size-3 mr-2 animate-spin" />
                                        ) : (
                                            <PlayIcon className="size-3 mr-2" />
                                        )}
                                        Sync Now
                                    </Button>
                                </div>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

// ── EOH File Upload ─────────────────────────────────────────

interface EohUploadResult {
    status: string;
    filename: string;
    totalRows: number;
    created: number;
    updated: number;
    skipped: number;
    error?: string;
}

function EohUploadSection({enabled, onComplete}: {enabled: boolean; onComplete: () => void}) {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState<EohUploadResult | null>(null);
    const [error, setError] = useState<string | null>(null);

    const handleUpload = useCallback(async (file: File) => {
        setUploading(true);
        setError(null);
        setResult(null);
        try {
            const csrfToken = Cookies.get("csrftoken") || "";
            const formData = new FormData();
            formData.append("file", file);
            const response = await fetch("/api/eoh/upload/", {
                method: "POST",
                headers: {"X-CSRFToken": csrfToken},
                credentials: "include",
                body: formData,
            });
            const json = await response.json();
            if (!response.ok) {
                throw new Error(json?.error || `Upload failed (${response.status})`);
            }
            setResult(json);
            onComplete();
        } catch (e) {
            setError(e instanceof Error ? e.message : "Upload failed");
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    }, [onComplete]);

    if (!enabled) return null;

    return (
        <div className="space-y-3">
            <div className="pt-3 pb-1">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Manual Upload</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                    Upload an EOH export file (.xlsx or .csv) to load shift data directly into the local cache.
                </p>
            </div>

            <Card>
                <CardContent className="p-4 space-y-3">
                    <div className="flex items-center gap-3">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".xlsx,.csv"
                            className="hidden"
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleUpload(file);
                            }}
                        />
                        <Button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            variant="outline"
                            size="sm"
                            className="h-8 text-xs"
                        >
                            {uploading ? (
                                <Loader2Icon className="size-3 mr-2 animate-spin" />
                            ) : (
                                <UploadIcon className="size-3 mr-2" />
                            )}
                            {uploading ? "Uploading..." : "Upload EOH File"}
                        </Button>
                        <span className="text-xs text-muted-foreground">
                            Excel with EOH_Data sheet, or CSV with Office, Client_Name, Qualification, Shift_Status, Start_Date, Booking_Hours columns
                        </span>
                    </div>

                    {error && (
                        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">
                            <AlertCircleIcon className="size-4 shrink-0" />
                            {error}
                        </div>
                    )}

                    {result && (
                        <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 rounded-md px-3 py-2">
                            <CheckCircleIcon className="size-4 shrink-0" />
                            <span>
                                <strong>{result.filename}</strong>: {result.created} created, {result.updated} updated
                                {result.skipped > 0 && `, ${result.skipped} skipped`}
                                {" "}({result.totalRows} total rows)
                            </span>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

// ── Main Tab ─────────────────────────────────────────────────

export function DataSyncTab({workspaceId, autoTriggerSyncAll}: DataSyncTabProps) {
    const [polling, setPolling] = useState(false);
    const {syncStatusAll, refresh} = useVNextSyncStatusAll(polling);
    const [syncAllTriggered, setSyncAllTriggered] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [eohStatus, setEohStatus] = useState<EohStatusData | null>(null);
    const [eohSyncStatus, setEohSyncStatus] = useState<EohSyncStatusData | null>(null);
    const [eohError, setEohError] = useState<string | null>(null);
    const [eohPolling, setEohPolling] = useState(false);
    const [eohStarting, setEohStarting] = useState<string | null>(null);
    const [eohLookbacks, setEohLookbacks] = useState<Record<string, number>>({
        bookings: 7,
        shifts: 7,
    });

    // Per-entity lookback months (only for entities with hasLookback)
    const [lookbacks, setLookbacks] = useState<Record<string, number>>({
        bank_transactions: 24,
        invoices: 24,
        journals: 24,
        report_profit_and_loss: 24,
        report_balance_sheet: 24,
        report_bank_summary: 24,
    });
    const setLookback = useCallback((et: string, months: number) => {
        setLookbacks((prev) => ({...prev, [et]: months}));
    }, []);
    const setEohLookback = useCallback((syncType: string, days: number) => {
        setEohLookbacks((prev) => ({...prev, [syncType]: days}));
    }, []);

    // Always build a full entity map so cards render even before the API responds
    const rawEntities = syncStatusAll?.entities ?? {};
    const entities: Record<string, EntitySyncStatus> = useMemo(() => {
        const result: Record<string, EntitySyncStatus> = {};
        for (const et of ENTITY_ORDER) {
            result[et] = rawEntities[et] ?? {
                entity_type: et,
                status: null,
                records_synced: 0,
                record_count: 0,
                started_at: null,
                completed_at: null,
                error_detail: null,
                is_complete: null,
                pages_fetched: null,
                needs_continuation: false,
            };
        }
        return result;
    }, [rawEntities]);
    const chainRunning = syncStatusAll?.chain_running ?? false;
    const anyRunning = useMemo(
        () => Object.values(entities).some((e) => e.status === "running"),
        [entities],
    );

    // Enable polling when any entity is running
    useEffect(() => {
        setPolling(anyRunning || chainRunning);
    }, [anyRunning, chainRunning]);

    const refreshEoh = useCallback(async (keepPolling = false) => {
        try {
            const statusResp = await fetch("/api/eoh/status/", {credentials: "include"});
            if (!statusResp.ok) return;
            const statusJson = await statusResp.json() as EohStatusData;
            setEohStatus(statusJson);
            if (!statusJson.enabled) {
                setEohSyncStatus(null);
                setEohPolling(false);
                return;
            }

            const syncResp = await fetch("/api/eoh/sync-status/", {credentials: "include"});
            if (!syncResp.ok) {
                const json = await syncResp.json().catch(() => null);
                throw new Error(json?.error || `EOH sync status failed (${syncResp.status})`);
            }
            const syncJson = await syncResp.json() as EohSyncStatusData;
            setEohSyncStatus(syncJson);
            setEohPolling(keepPolling || syncJson.isRunning);
            setEohError(null);
        } catch (e) {
            setEohError(e instanceof Error ? e.message : "Failed to load EOH sync status");
        }
    }, []);

    useEffect(() => {
        refreshEoh();
    }, [refreshEoh]);

    const processEohQueue = useCallback(async () => {
        try {
            const csrfToken = Cookies.get("csrftoken") || "";
            await fetch("/api/eoh/process-sync-queue/", {
                method: "POST",
                headers: {"Content-Type": "application/json", "X-CSRFToken": csrfToken},
                credentials: "include",
            });
        } catch (e) {
            setEohError(e instanceof Error ? e.message : "Failed to process EOH sync queue");
        }
    }, []);

    useEffect(() => {
        if (!eohPolling) return;
        const id = window.setInterval(async () => {
            await processEohQueue();
            refreshEoh();
        }, 10000);
        return () => window.clearInterval(id);
    }, [eohPolling, refreshEoh, processEohQueue]);

    // Auto-trigger sync-all on mount if requested
    useEffect(() => {
        if (autoTriggerSyncAll && !syncAllTriggered && !anyRunning && !chainRunning) {
            setSyncAllTriggered(true);
            triggerSyncAll(workspaceId)
                .then(() => {
                    setPolling(true);
                    refresh();
                })
                .catch((e) => setError(e instanceof Error ? e.message : "Failed to start sync"));
        }
    }, [autoTriggerSyncAll, syncAllTriggered, anyRunning, chainRunning, refresh]);

    const handleSyncAll = useCallback(async () => {
        setError(null);
        try {
            await triggerSyncAll(workspaceId);
            setPolling(true);
            refresh();
        } catch (e) {
            setError(e instanceof Error ? e.message : "Failed");
        }
    }, [refresh]);

    const handleEntitySync = useCallback(
        async (entityType: string) => {
            setError(null);
            try {
                const lb = lookbacks[entityType];
                await triggerEntitySync(entityType, lb);
                setPolling(true);
                refresh();
            } catch (e) {
                setError(e instanceof Error ? e.message : "Failed");
            }
        },
        [refresh, lookbacks],
    );

    const handleStop = useCallback(
        async (entityType: string) => {
            try {
                await stopEntitySync(entityType);
                refresh();
            } catch (e) {
                setError(e instanceof Error ? e.message : "Failed to stop");
            }
        },
        [refresh],
    );

    const handleClear = useCallback(
        async (entityType: string) => {
            try {
                await clearEntitySync(entityType);
                refresh();
            } catch (e) {
                setError(e instanceof Error ? e.message : "Failed to clear");
            }
        },
        [refresh],
    );

    const handleStopAll = useCallback(async () => {
        try {
            await stopEntitySync("all");
            refresh();
        } catch (e) {
            setError(e instanceof Error ? e.message : "Failed to stop");
        }
    }, [refresh]);

    const handleEohSync = useCallback(
        async (entityType: string, lookbackDays?: number) => {
            setEohError(null);
            setEohStarting(entityType);
            try {
                const csrfToken = Cookies.get("csrftoken") || "";
                const response = await fetch("/api/eoh/sync-entity/", {
                    method: "POST",
                    headers: {"Content-Type": "application/json", "X-CSRFToken": csrfToken},
                    credentials: "include",
                    body: JSON.stringify({entity_type: entityType, lookback_days: lookbackDays ?? 7}),
                });
                if (!response.ok) {
                    const json = await response.json().catch(() => null);
                    throw new Error(json?.error || `EOH sync failed (${response.status})`);
                }
                setEohPolling(true);
                await refreshEoh(true);
            } catch (e) {
                setEohError(e instanceof Error ? e.message : "Failed to start EOH sync");
            } finally {
                setEohStarting(null);
            }
        },
        [refreshEoh],
    );

    const handleEohForwardSync = useCallback(
        async (daysAhead = 90) => {
            setEohError(null);
            setEohStarting("shifts");
            try {
                const csrfToken = Cookies.get("csrftoken") || "";
                const response = await fetch("/api/eoh/sync-entity/", {
                    method: "POST",
                    headers: {"Content-Type": "application/json", "X-CSRFToken": csrfToken},
                    credentials: "include",
                    body: JSON.stringify({entity_type: "shifts", days_ahead: daysAhead}),
                });
                if (!response.ok) {
                    const json = await response.json().catch(() => null);
                    throw new Error(json?.error || `Forward sync failed (${response.status})`);
                }
                setEohPolling(true);
                await refreshEoh(true);
            } catch (e) {
                setEohError(e instanceof Error ? e.message : "Failed to start forward sync");
            } finally {
                setEohStarting(null);
            }
        },
        [refreshEoh],
    );

    const handleEohCancel = useCallback(
        async (entityType?: string) => {
            setEohError(null);
            try {
                const csrfToken = Cookies.get("csrftoken") || "";
                const response = await fetch("/api/eoh/sync-cancel/", {
                    method: "POST",
                    headers: {"Content-Type": "application/json", "X-CSRFToken": csrfToken},
                    credentials: "include",
                    body: JSON.stringify({entity_type: entityType ?? "all"}),
                });
                if (!response.ok) {
                    const json = await response.json().catch(() => null);
                    throw new Error(json?.error || `Cancel failed (${response.status})`);
                }
                await refreshEoh();
            } catch (e) {
                setEohError(e instanceof Error ? e.message : "Failed to cancel EOH sync");
            }
        },
        [refreshEoh],
    );

    // Progress counters
    const completedCount = useMemo(
        () => ENTITY_ORDER.filter((et) => {
            const s = entities[et]?.status;
            return s === "success" || s === "partial";
        }).length,
        [entities],
    );
    const currentRunning = useMemo(
        () => ENTITY_ORDER.map((et) => entities[et]).find((e) => e?.status === "running"),
        [entities],
    );

    // Total records
    const totalRecords = useMemo(
        () => Object.values(entities).reduce((sum, e) => sum + (e.record_count || 0), 0),
        [entities],
    );
    const lastSync = useMemo(() => {
        const dates = Object.values(entities)
            .map((e) => e.completed_at)
            .filter(Boolean) as string[];
        if (dates.length === 0) return null;
        return dates.sort().pop()!;
    }, [entities]);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-lg font-semibold">Xero Data Sync</h2>
                    <p className="text-sm text-muted-foreground mt-0.5">
                        Sync each data type individually, or run all at once. Each sync can be stopped independently.
                    </p>
                    {(totalRecords > 0 || lastSync) && (
                        <div className="flex items-center gap-3 mt-1.5">
                            {totalRecords > 0 && (
                                <span className="text-xs text-muted-foreground">
                                    {formatNumber(totalRecords)} total records synced
                                </span>
                            )}
                            {lastSync && (
                                <span className="text-xs text-muted-foreground">
                                    Last sync {formatRelativeTime(lastSync)}
                                </span>
                            )}
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {(anyRunning || chainRunning) && currentRunning && (
                        <span className="text-xs text-blue-600">
                            Syncing {ENTITY_META[currentRunning.entity_type]?.label ?? currentRunning.entity_type}... ({completedCount}/{ENTITY_ORDER.length})
                        </span>
                    )}
                    {(anyRunning || chainRunning) && (
                        <Button
                            onClick={handleStopAll}
                            variant="outline"
                            size="sm"
                            className="text-amber-600 border-amber-300 hover:bg-amber-50"
                        >
                            <SquareIcon className="size-4 mr-2" />
                            Stop All
                        </Button>
                    )}
                    <Button
                        onClick={handleSyncAll}
                        disabled={anyRunning || chainRunning}
                        className="bg-[#209DB3] hover:bg-[#1a8a9e] text-white"
                        size="sm"
                    >
                        {anyRunning || chainRunning ? (
                            <Loader2Icon className="size-4 mr-2 animate-spin" />
                        ) : (
                            <PlayIcon className="size-4 mr-2" />
                        )}
                        Sync All
                    </Button>
                </div>
            </div>

            {error && (
                <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">
                    <AlertCircleIcon className="size-4 shrink-0" />
                    {error}
                </div>
            )}

            {/* Step-by-step sync rows */}
            <div className="space-y-3">
                {ENTITY_ORDER.map((et, i) => (
                    <div key={et}>
                        {/* Section header before report entities */}
                        {i === REPORT_SECTION_START && (
                            <div className="pt-3 pb-1">
                                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Xero Reports</h3>
                                <p className="text-xs text-muted-foreground mt-0.5">Pre-calculated reports for check figures and reconciliation</p>
                            </div>
                        )}
                        <SyncStepRow
                            stepNumber={i + 1}
                            entity={entities[et]}
                            meta={ENTITY_META[et]}
                            lookbackMonths={lookbacks[et] ?? 24}
                            onLookbackChange={(months) => setLookback(et, months)}
                            onSync={() => handleEntitySync(et)}
                            onStop={() => handleStop(et)}
                            onClear={() => handleClear(et)}
                        />
                    </div>
                ))}
            </div>

            <EohSyncSection
                status={eohStatus}
                syncStatus={eohSyncStatus}
                error={eohError}
                onSync={handleEohSync}
                onForwardSync={handleEohForwardSync}
                onCancel={handleEohCancel}
                isStarting={eohStarting}
                lookbacks={eohLookbacks}
                onLookbackChange={setEohLookback}
            />
            <EohUploadSection
                enabled={eohStatus?.enabled ?? false}
                onComplete={() => refreshEoh()}
            />
        </div>
    );
}
